import React, { useEffect, useRef, useState } from "react";
import { TextField, InputAdornment } from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";

/**
 * A debounced search bar component.
 *
 * @param {string} [placeholder="search..."] - The Placeholder text.
 * @param {string} searchText - The current value of the search input.
 * @param {Function} [onSearchChange=() => {}] - The function to call after a delay when the search input changes.
 * @param {boolean} [disabled=false] - Whether the search input is disabled.
 * @returns {import("react").ReactElement} The component
 */
export const SearchBar = ({ placeholder = "search...", searchText, onSearchChange = () => {}, disabled = false }) => {
  const [searchValue, setSearchValue] = useState(searchText);
  const timeoutId = useRef(null); // Preserve timeoutId across re-renders
  const lastSentSearch = useRef(searchText);

  useEffect(() => {
    // Cleanup function to prevent memory leaks
    return () => {
      if (timeoutId.current) {
        clearTimeout(timeoutId.current);
      }
    };
  }, []);

  // Follow changes made outside the input (e.g. a remembered search restored on Back). The echo of our own
  // debounced update is skipped, so text typed since is kept; an outside change also drops pending keystrokes.
  useEffect(() => {
    if (searchText === lastSentSearch.current) return;
    lastSentSearch.current = searchText;
    if (timeoutId.current) clearTimeout(timeoutId.current);
    setSearchValue(searchText);
  }, [searchText]);

  const handleSearchChange = (event) => {
    const value = event.target.value;
    setSearchValue(value);
    // Clear any previous timeouts
    if (timeoutId.current) {
      clearTimeout(timeoutId.current);
    }
    // Set a new timeout to call onSearchChange after 500ms
    timeoutId.current = setTimeout(() => {
      lastSentSearch.current = value;
      onSearchChange(value);
    }, 500);
  };

  return (
    <TextField
      id="outlined-basic"
      placeholder={placeholder}
      type="search"
      variant="outlined"
      autoComplete="off"
      value={searchValue}
      onChange={handleSearchChange}
      disabled={disabled}
      size="small"
      sx={{
        "& .MuiOutlinedInput-root": {
          backgroundColor: "#fff",
          borderRadius: "10px",
          "&:hover fieldset": {
            borderColor: "#674BF5",
          },
          "&.Mui-focused fieldset": {
            borderColor: "#674BF5",
          },
        },
      }}
      slotProps={{
        input: {
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon sx={{ fontSize: "1.25rem" }} />
            </InputAdornment>
          ),
        },
      }}
    />
  );
};
